// Environment variables — these are deployment/infrastructure concerns.
// They vary by ENVIRONMENT (dev/staging/prod), not by tenant.
//
// In the generic build, REACT_APP_* are no longer baked in at build time.
// Instead, 00-geo-nginx.sh generates /env-config.js at container startup,
// which sets window.__ENV__ with the container's environment variables.
// We read from window.__ENV__ first, with process.env as fallback (dev mode).
const runtimeEnv = (typeof window !== 'undefined' && window.__ENV__) || {};
const env = (key) => runtimeEnv[key] || process.env[key] || '';

// --- Mutable tenant bindings ---
// These use `export let` so that when updated by the resolution flow
// or by applyRuntimeEnv(), every module that imported them sees the
// new value (ES module live bindings).
export let tenantId = env('REACT_APP_TENANT_ID');
export let tenantName = env('REACT_APP_TENANT_NAME');

/**
 * Called by the tenant resolution flow to update tenantId/tenantName
 * at runtime based on hostname. All modules importing these named exports
 * will see the updated values thanks to ES module live bindings.
 */
export function setTenantIdentity(id, name) {
  tenantId = id;
  tenantName = name;
}

// --- Immutable environment config ---
// Empty in production generic builds (same-origin /api via nginx). Use for XHR/fetch.
export const backendUrl = env('REACT_APP_BACKEND_URL');
// Real Nest base URL for EventSource/SSE only (set by 00-geo-nginx.sh at runtime).
export const sseBackendUrl = env('REACT_APP_SSE_BACKEND_URL') || backendUrl;

// Google Tag Manager (platform-level, same across tenants in same env)
// Populated at runtime by BFF via applyRuntimeEnv().
export let gtmId = '';
export let gtmAuth = '';
export let gtmName = '';

// --- Mutable tenant-specific config ---
// Populated at runtime by BFF via applyRuntimeEnv().
export let tenantGtmId = '';
export let ecommerceUrl = '';
export let authExternalUrl = '';
export let onboardingUrl = '';
export let offboardingUrl = '';
export let helpUrl = '';
export let checkoutUrl = '';
export let buyReaderUrl = '';
export let termsAndConditionsUrl = '';
export let isGoogleCaptchaEnabled = '';
export let recaptchaSiteKey = '';
export let chatBotId = '';

/**
 * Apply tenant-specific env config received from the BFF.
 * Called from resolveTenant (visual-config, pre-login) and
 * from ConfigProvider (bootstrap, post-login).
 */
export function applyRuntimeEnv(data) {
  if (!data) return;
  if (data.gtmId) gtmId = data.gtmId;
  if (data.gtmAuth) gtmAuth = data.gtmAuth;
  if (data.gtmName) gtmName = data.gtmName;
  if (data.tenantGtmId) tenantGtmId = data.tenantGtmId;
  if (data.ecommerceUrl) ecommerceUrl = data.ecommerceUrl;
  if (data.onboardingUrl) onboardingUrl = data.onboardingUrl;
  if (data.offboardingUrl) offboardingUrl = data.offboardingUrl;
  if (data.helpUrl) helpUrl = data.helpUrl;
  if (data.checkoutUrl) checkoutUrl = data.checkoutUrl;
  if (data.buyReaderLink) buyReaderUrl = data.buyReaderLink;
  if (data.termsAndConditionsUrl) termsAndConditionsUrl = data.termsAndConditionsUrl;
  if (data.chatBotId) chatBotId = data.chatBotId;
  if (data.recaptchaEnabled !== undefined) isGoogleCaptchaEnabled = String(data.recaptchaEnabled);
  if (data.recaptchaSiteKey) recaptchaSiteKey = data.recaptchaSiteKey;
}

// Infrastructure keys
export const googleMapsApiKey = env('GOOGLE_MAPS_API_KEY');
export const cryptoKey = env('REACT_APP_CRYPTO_KEY');
export const loginPublicKey =
  "-----BEGIN PUBLIC KEY-----\n" +
  "MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQCFaZga7Ot3UuKNvm5YreE3LhGS\n" +
  "GAdTpVo0junajWABhCnGUjxZbrSL6QamzvA18Wx+HuJDIFTG47EMOMUld263HF3f\n" +
  "1kNX9LLPBsti0v+N1Yk80xG2eM88hlVDA2f3sDod+JZ1AT+sOTwn35FZy/qv7nZw\n" +
  "JTrUGLIKiCGrACI23QIDAQAB\n" +
  "-----END PUBLIC KEY-----";

// Default export for modules that use `import envConfig from 'config/envConfig'`
// Note: mutable fields use getters so they always reflect current values.
const envConfig = {
  get tenantId() { return tenantId; },
  get tenantName() { return tenantName; },
  backendUrl,
  sseBackendUrl,
  get gtmId() { return gtmId; },
  get gtmAuth() { return gtmAuth; },
  get gtmName() { return gtmName; },
  get tenantGtmId() { return tenantGtmId; },
  get ecommerceUrl() { return ecommerceUrl; },
  get authExternalUrl() { return authExternalUrl; },
  get onboardingUrl() { return onboardingUrl; },
  get offboardingUrl() { return offboardingUrl; },
  get helpUrl() { return helpUrl; },
  get checkoutUrl() { return checkoutUrl; },
  get buyReaderUrl() { return buyReaderUrl; },
  get termsAndConditionsUrl() { return termsAndConditionsUrl; },
  googleMapsApiKey,
  cryptoKey,
  loginPublicKey,
  get isGoogleCaptchaEnabled() { return isGoogleCaptchaEnabled; },
  get recaptchaSiteKey() { return recaptchaSiteKey; },
  get chatBotId() { return chatBotId; },
};

export default envConfig;
